import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { STUDIO_IMAGE, STYLES } from "@/data/content";

const TeaserImage = () => (
    <Reveal className="lg:col-span-5">
        <div className="relative">
            <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-wood/15" />
            <div className="group relative overflow-hidden">
                <img
                    src={STUDIO_IMAGE.src}
                    alt={STUDIO_IMAGE.alt}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{STUDIO_IMAGE.caption}</p>
        </div>
    </Reveal>
);

const StyleStrip = () => (
    <div className="mt-10 grid grid-cols-2 gap-px border border-wood/10 bg-wood/10 sm:grid-cols-4">
        {STYLES.map((s) => (
            <div key={s.number} className="bg-ivory p-4">
                <span className="font-mono text-[10px] text-terra">{s.number}</span>
                <p className="mt-2 font-serif text-sm font-medium text-ink sm:text-base">{s.title}</p>
            </div>
        ))}
    </div>
);

const TeaserCopy = () => (
    <div className="lg:col-span-7">
        <Reveal>
            <div className="flex items-center gap-4">
                <span className="font-mono text-sm text-terra">01</span>
                <span className="h-px w-10 bg-terra/50" />
                <span className="text-xs uppercase tracking-[0.25em] text-timber">Who We Are</span>
            </div>
        </Reveal>
        <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
                A Studio Where Design Meets Engineering
            </h2>
        </Reveal>
        <Reveal delay={0.2} className="mt-8 max-w-xl space-y-6 text-base font-light leading-relaxed text-mute sm:text-lg">
            <p>
                NoirNest is a multidisciplinary studio led by Architects and Engineers. For over 10
                years, we have shaped residential and commercial environments—not just to look
                complete, but to feel complete.
            </p>
            <p className="font-serif text-xl italic text-wood sm:text-2xl">“We don’t begin with layouts or catalogs. We begin with you.”</p>
        </Reveal>
        <Reveal delay={0.3}>
            <StyleStrip />
            <Link
                to="/studio"
                data-testid="home-studio-link"
                className="group mt-10 inline-flex items-center gap-3 rounded-full border border-wood/25 px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-ivory"
            >
                Our Philosophy & Story
                <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
        </Reveal>
    </div>
);

export const StudioTeaser = () => (
    <section id="intro" data-testid="home-studio-teaser" className="relative overflow-hidden py-20 lg:py-32">
        <span aria-hidden className="pointer-events-none absolute -top-6 right-4 select-none font-serif text-[10rem] leading-none text-wood/[0.05] lg:text-[17rem]">
            01
        </span>
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
            <TeaserImage />
            <TeaserCopy />
        </div>
    </section>
);
